import { useState } from 'react';
import {
  MAX_TRAITS,
  NEEDS,
  NEED_KEYS,
  TRAITS,
  TRAIT_KEYS,
  feelingsOf,
  moodOf,
  moodPoints,
  type LifeView,
  type Needs,
  type PlayState,
  type TraitKey,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { needColour } from '../play/needs';

const longDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

// how the character is doing: the six needs, the feelings they cause (with reasons and mood points), and the
// personality traits that change how needs move
export function NeedsTab({
  life,
  play,
  needs,
  onLife,
}: {
  life: LifeView;
  play: PlayState;
  needs: Needs;
  onLife: (life: LifeView, message?: string) => void;
}) {
  const feelings = feelingsOf(needs, {
    lastPaidAt: play.lastPaidAt,
    lifeStartedAt: play.lifeStartedAt,
    sick: play.sick,
  });
  const points = moodPoints(feelings);
  const mood = moodOf(needs);
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<TraitKey[]>(life.traits as TraitKey[]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const toggle = (trait: TraitKey) =>
    setChosen((list) =>
      list.includes(trait) ? list.filter((item) => item !== trait) : list.length < MAX_TRAITS ? [...list, trait] : list,
    );
  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const reply = await api.life.traits({ traits: chosen });
      if (reply.life) onLife(reply.life, 'Traits saved');
      setPicking(false);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  const traits = life.traits as TraitKey[];
  return (
    <div className="profile-grid">
      <section className="profile-card" aria-labelledby="needs-title">
        <h3 id="needs-title">
          Needs{' '}
          <span className="profile-mood">
            {mood.icon} {mood.label}
          </span>
        </h3>
        <ul className="needs-list">
          {NEED_KEYS.map((key) => (
            <li key={key}>
              <span className="needs-name">
                <em aria-hidden="true">{NEEDS[key].icon}</em> {NEEDS[key].label}
              </span>
              <span
                className="needs-bar"
                role="meter"
                aria-label={NEEDS[key].label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(needs[key])}
              >
                <b style={{ width: `${needs[key]}%`, background: needColour(needs[key]) }} />
              </span>
              <span className="needs-value">{Math.round(needs[key])}%</span>
            </li>
          ))}
        </ul>
        <p className="profile-hint">Needs fall with time. Eat, sleep, wash, have fun, see people, and find a loo.</p>
      </section>

      <section className="profile-card" aria-labelledby="feelings-title">
        <h3 id="feelings-title">
          Feelings{' '}
          <span className={points >= 0 ? 'profile-points good' : 'profile-points bad'}>
            {points > 0 ? '+' : ''}
            {points} mood
          </span>
        </h3>
        {feelings.length === 0 ? (
          <p className="profile-hint">Nothing in particular right now: doing fine.</p>
        ) : (
          <ul className="feelings-list">
            {feelings.map((feeling) => (
              <li key={feeling.key}>
                <em aria-hidden="true">{feeling.icon}</em>
                <span>
                  <strong>{feeling.label}</strong>
                  <small>{feeling.reason}</small>
                </span>
                <span className={feeling.points >= 0 ? 'profile-points good' : 'profile-points bad'}>
                  {feeling.points > 0 ? '+' : ''}
                  {feeling.points}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="profile-card" aria-labelledby="traits-title">
        <h3 id="traits-title">Traits</h3>
        {!picking ? (
          <>
            {traits.length === 0 ? (
              <p className="profile-hint">Pick up to {MAX_TRAITS} traits that suit your character.</p>
            ) : (
              <ul className="traits-list">
                {traits.map((trait) => (
                  <li key={trait}>
                    <em aria-hidden="true">{TRAITS[trait].icon}</em>
                    <span>
                      <strong>{TRAITS[trait].label}</strong>
                      <small>{TRAITS[trait].description}</small>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(life.traitsChangeFrom)}
              onClick={() => {
                setChosen(traits);
                setPicking(true);
              }}
            >
              {traits.length ? 'Change traits' : 'Choose traits'}
            </Button>
            <p className="profile-hint">
              {life.traitsChangeFrom
                ? `You can change your traits again from ${longDate(life.traitsChangeFrom)}.`
                : 'Traits can be changed once a week.'}
            </p>
          </>
        ) : (
          <>
            <p className="profile-hint">
              Choose up to {MAX_TRAITS}. {chosen.length} chosen.
            </p>
            <div className="traits-picker">
              {TRAIT_KEYS.map((trait) => (
                <button
                  type="button"
                  key={trait}
                  aria-pressed={chosen.includes(trait)}
                  disabled={!chosen.includes(trait) && chosen.length >= MAX_TRAITS}
                  onClick={() => toggle(trait)}
                >
                  <em aria-hidden="true">{TRAITS[trait].icon}</em>
                  <strong>{TRAITS[trait].label}</strong>
                  <small>{TRAITS[trait].description}</small>
                </button>
              ))}
            </div>
            {failure && <ErrorNotice error={failure} />}
            <div className="profile-actions">
              <Button type="button" variant="outline" onClick={() => setPicking(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save traits'}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
